/**
 * AI Drafts wire shape + hooks (Phase X.3.1 → X.3.4.5).
 *
 * X.3.1 shipped the page shell with a `useAiDraftsStub` returning [].
 * X.3.4.5 replaces the stub with `useAiDrafts(slug)` which fetches via
 * /api/t/{slug}/v1/ai-tools/drafts. The stub is retained for tests that
 * want a bypass without TanStack Query.
 *
 * Tool enum mirrors ai_drafts.tool_name (assessment / intake / email /
 * brand_formatter / med_list). A med_list draft may be linked to a client
 * (client_id), which the record's Documents tab reads through the same
 * hook narrowed by client + tool.
 */

import { useQuery } from '@tanstack/react-query';
import { aiToolsApi, type AiDraftSummaryWire, type AiDraftDetailWire } from '@/lib/api';

export interface AiDraftSummary {
  id: string;
  tool_name: 'assessment' | 'intake' | 'email' | 'brand_formatter' | 'med_list';
  status: 'draft' | 'finalized' | 'archived';
  /** The linked client, or null for a free-text draft. */
  client_id: string | null;
  created_at: string;
  preview: string;
  // X.3.4.11: author name, present only on the owner all-view (null on
  // own-view). DraftsList renders an attribution chip when set.
  created_by_name: string | null;
}

/**
 * X.3.1 stub retained for tests that don't need TanStack Query bootstrap.
 * Production renderers use `useAiDrafts` below.
 */
export function useAiDraftsStub(): readonly AiDraftSummary[] {
  return [];
}

/**
 * Real fetch hook. Lists every draft visible to the calling user across
 * all tools, newest-first, per the repo-layer visibility model in
 * api/_shared/repos/aiDrafts.ts:visibilityCondition.
 *
 * Background refetch: TanStack Query staleTime=30s (matches the existing
 * admin-review badge-count cadence). No explicit polling.
 *
 * `enabled` defaults to true. Callers that need to keep the hook in
 * stable position above a capability-gate early-return pass
 * `enabled: canUseAiTools(whoami)` to suppress the fetch when the user
 * is not cap-eligible. The Rules-of-Hooks pattern requires the hook
 * call itself to always run; only the fetch is conditional.
 */
export function useAiDrafts(
  tenantSlug: string,
  opts: {
    enabled?: boolean;
    scope?: 'own' | 'all';
    /** Only drafts linked to this client (the record-side list). */
    client_id?: string;
    /** Only one tool's drafts. */
    tool?: AiDraftSummary['tool_name'];
  } = {},
): {
  drafts: readonly AiDraftSummary[];
  isLoading: boolean;
  isError: boolean;
} {
  const enabled = opts.enabled ?? true;
  // X.3.4.11: scope in the queryKey so own/all are cached separately.
  // Mutations invalidate the ['ai-tools','drafts',slug] prefix, which
  // still clears both scope entries.
  const scope = opts.scope ?? 'own';
  const client_id = opts.client_id;
  const tool = opts.tool;
  const query = useQuery({
    queryKey: ['ai-tools', 'drafts', tenantSlug, scope, client_id ?? null, tool ?? null],
    queryFn: () => aiToolsApi.listDrafts(tenantSlug, { scope, client_id, tool }),
    staleTime: 30_000,
    enabled,
  });
  const drafts: readonly AiDraftSummary[] = (query.data?.rows ?? []).map(
    (r: AiDraftSummaryWire) => ({
      id: r.id,
      tool_name: r.tool_name,
      status: r.status,
      client_id: r.client_id ?? null,
      created_at: r.created_at,
      preview: r.preview,
      created_by_name: r.created_by_name,
    }),
  );
  return {
    drafts,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * X.3.4.7 single-draft detail fetch hook, backing the drafts-inbox
 * click-through page. Keyed ['ai-tools', 'draft', tenantSlug, id] —
 * sibling of the list key ['ai-tools', 'drafts', tenantSlug]. Mutations
 * on the detail page invalidate BOTH keys (the mutation response shape
 * differs from the cached query shape, so invalidate rather than
 * setQueryData).
 *
 * `error` is exposed so the page can distinguish a 404 (render a friendly
 * "no longer available" line) from a generic load error.
 */
export function useAiDraft(
  tenantSlug: string,
  draftId: string,
  opts: { enabled?: boolean } = {},
): {
  draft: AiDraftDetailWire | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const enabled = opts.enabled ?? true;
  const query = useQuery({
    queryKey: ['ai-tools', 'draft', tenantSlug, draftId],
    queryFn: () => aiToolsApi.getDraft(tenantSlug, draftId),
    enabled: enabled && draftId.length > 0,
  });
  return {
    draft: query.data?.draft,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
